import { ContractTable } from '@dealstack/frontend/shared/components/Investors/Substeps/ContractTable/ContractTable';
import { SecurityTable } from '@dealstack/frontend/shared/components/Investors/Substeps/SecurityTable/SecurityTable';
import { UploadFile } from '@dealstack/frontend/shared/components/Investors/Substeps/UploadFile/UploadFile';
import { VestingTable } from '@dealstack/frontend/shared/components/Investors/Substeps/VestingTable/VestingTable';
import {
	INVESTORS_SUBSTEPS,
	useInvestors,
} from '@dealstack/frontend/shared/components/Investors/useInvestors';
import { OverlaidTooltip } from '@dealstack/frontend/shared/components/OverlaidTooltip/OverlaidTooltip';
import { ForwardOnlySubstepMenu } from '@dealstack/frontend/shared/components/SubstepMenu/ForwardOnlySubstepMenu';
import { HelpTooltipContent } from '@dealstack/frontend/shared/components/Investors/HelpTooltipContent';
import { renderMIPCreateSectionHeaderPortal } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/mipCreatePortals';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Button, Text } from '@mantine/core';

interface Props {
	navigate: (link: string) => void;
	refreshStatuses: () => void;
}

export const Investors = ({ navigate, refreshStatuses }: Props) => {
	const {
		commonUiTranslation,
		steps,
		currentStep,
		goToNextSubstep,
		resetSubsteps,
		changeStepTo,
		isOpen,
		toggleTooltip,
		onUpload,
		substepDisableNext,
		investors,
		contracts,
		vestingTerms,
		eligibleSteps,
		onTemplateDownload,
		contractsChecked,
		vestingTermsChosen,
		onContractCheckboxClick,
		onVestingTermChange,
		areAllInvestorsSelected,
		onContractSelectAll,
		updateInvestorLoading,
	} = useInvestors(navigate, refreshStatuses);

	const substepComponents = {
		[INVESTORS_SUBSTEPS.UPLOAD]: (
			<UploadFile onUpload={onUpload} onTemplateDownload={onTemplateDownload} />
		),
		[INVESTORS_SUBSTEPS.SECURITIES]: <SecurityTable investors={investors} />,
		[INVESTORS_SUBSTEPS.CONTRACTS]: (
			<ContractTable
				investors={investors}
				contracts={contracts}
				contractsChecked={contractsChecked}
				onContractCheckboxClick={onContractCheckboxClick}
				areAllInvestorsSelected={areAllInvestorsSelected}
				onContractSelectAll={onContractSelectAll}
			/>
		),
		[INVESTORS_SUBSTEPS.VESTING]: (
			<VestingTable
				investors={investors}
				vestingTerms={vestingTerms}
				vestingTermsChosen={vestingTermsChosen}
				onVestingTermChange={onVestingTermChange}
			/>
		),
	};

	return (
		<div className="flex flex-col h-full">
			{renderMIPCreateSectionHeaderPortal(
				<SectionHeader title={commonUiTranslation('investors.header.title')} />
			)}
			<div className="flex justify-between">
				<div>
					{currentStep?.id !== INVESTORS_SUBSTEPS.UPLOAD && (
						<Button
							size="md"
							variant="default"
							onClick={resetSubsteps}
							c="dark.4"
							leftSection={<Icon variant="close" size="vlg" />}
							sx={(theme) => ({
								'& svg': {
									color: theme.colors.blue[6],
								},
							})}
						>
							<Text size="B6Bold" fw={600}>
								{commonUiTranslation('investors.reset_investors')}
							</Text>
						</Button>
					)}
				</div>
				<ForwardOnlySubstepMenu
					steps={steps}
					eligibleSteps={eligibleSteps}
					changeStepTo={changeStepTo}
					currentStep={currentStep}
					onNextClick={goToNextSubstep}
					disableNext={substepDisableNext}
					disableAll={updateInvestorLoading}
				/>
			</div>
			{currentStep ? substepComponents[currentStep.id] : null}
			<div className="flex justify-end mt-auto mb-4">
				<OverlaidTooltip
					title={commonUiTranslation('investors.help.title')}
					isOpen={isOpen}
					toggleTooltip={toggleTooltip}
					children={<HelpTooltipContent />}
				/>
			</div>
		</div>
	);
};
